import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';

type ParticleKind = 'main' | 'satellite';

@Component({
  selector: 'app-emoji-trail',
  standalone: true,
  templateUrl: './emoji-trail.html',
  styleUrl: './emoji-trail.scss',
  encapsulation: ViewEncapsulation.None
})
export class EmojiTrail implements AfterViewInit, OnDestroy {
  @ViewChild('trailRoot', { static: true })
  private trailRoot!: ElementRef<HTMLElement>;

  private readonly spawnDistance = 26;
  private readonly maxParticles = 78;

  private lastX = 0;
  private lastY = 0;
  private hasLastPoint = false;

  private mouseMoveHandler: ((event: MouseEvent) => void) | null = null;

  private activeElements: HTMLElement[] = [];
  private pooledElements: HTMLElement[] = [];
  private timeoutIds = new Map<HTMLElement, number>();

  private readonly gradients = [
    'linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(214, 214, 214, 0.78))',
    'linear-gradient(135deg, rgba(246, 246, 246, 0.92), rgba(172, 172, 172, 0.64))',
    'linear-gradient(135deg, rgba(255, 255, 255, 0.86), rgba(128, 128, 128, 0.52))',
    'linear-gradient(135deg, rgba(220, 220, 220, 0.78), rgba(255, 255, 255, 0.96))',
    'linear-gradient(135deg, rgba(238, 238, 238, 0.82), rgba(150, 150, 150, 0.48))'
  ];

  constructor(private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    const hasTouchPointer = window.matchMedia(
      '(hover: none), (pointer: coarse)'
    ).matches;

    if (hasTouchPointer) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.mouseMoveHandler = (event: MouseEvent) => {
        this.onMouseMove(event);
      };

      window.addEventListener('mousemove', this.mouseMoveHandler, {
        passive: true
      });
    });
  }

  ngOnDestroy(): void {
    if (this.mouseMoveHandler) {
      window.removeEventListener('mousemove', this.mouseMoveHandler);
      this.mouseMoveHandler = null;
    }

    this.timeoutIds.forEach(timeoutId => {
      window.clearTimeout(timeoutId);
    });

    this.timeoutIds.clear();

    this.activeElements.forEach(element => element.remove());
    this.pooledElements.forEach(element => element.remove());

    this.activeElements = [];
    this.pooledElements = [];
  }

  private onMouseMove(event: MouseEvent): void {
    if (!this.hasLastPoint) {
      this.lastX = event.clientX;
      this.lastY = event.clientY;
      this.hasLastPoint = true;
      return;
    }

    const dx = event.clientX - this.lastX;
    const dy = event.clientY - this.lastY;
    const distance = Math.hypot(dx, dy);

    if (distance < this.spawnDistance) {
      return;
    }

    this.lastX = event.clientX;
    this.lastY = event.clientY;

    this.createParticle(
      event.clientX,
      event.clientY,
      dx,
      dy,
      'main'
    );

    const satelliteCount = distance > 96
      ? 4
      : distance > 64
        ? 3
        : distance > 38
          ? 2
          : Math.random() > 0.35
            ? 1
            : 0;

    for (let i = 0; i < satelliteCount; i++) {
      this.createParticle(
        event.clientX,
        event.clientY,
        dx,
        dy,
        'satellite'
      );
    }
  }

  private createParticle(
    cursorX: number,
    cursorY: number,
    dx: number,
    dy: number,
    kind: ParticleKind
  ): void {
    const movementLength = Math.hypot(dx, dy) || 1;

    const backX = -dx / movementLength;
    const backY = -dy / movementLength;

    const spreadAngle = this.random(
      kind === 'main' ? -18 : -72,
      kind === 'main' ? 18 : 72
    ) * (Math.PI / 180);

    const cos = Math.cos(spreadAngle);
    const sin = Math.sin(spreadAngle);

    const directionX = backX * cos - backY * sin;
    const directionY = backX * sin + backY * cos;

    const sideX = -backY;
    const sideY = backX;

    const sideOffset = kind === 'main'
      ? this.random(-14, 14)
      : this.random(-46, 46);

    const flyDistance = kind === 'main'
      ? this.random(28, 74)
      : this.random(42, 128);

    const tx = directionX * flyDistance;
    const ty = directionY * flyDistance;

    const element = this.getParticleElement();

    const x =
      cursorX +
      backX * this.random(kind === 'main' ? 2 : 4, kind === 'main' ? 7 : 16) +
      sideX * sideOffset +
      this.random(kind === 'main' ? -2 : -8, kind === 'main' ? 2 : 8);

    const y =
      cursorY +
      backY * this.random(kind === 'main' ? 2 : 4, kind === 'main' ? 7 : 16) +
      sideY * sideOffset +
      this.random(kind === 'main' ? -2 : -8, kind === 'main' ? 2 : 8);

    const size = kind === 'main'
      ? this.random(24, 42)
      : this.random(8, 26);

    const spinDirection = Math.random() > 0.5 ? 1 : -1;

    const letterRotateStart = this.random(-180, 180);

    const letterRotateImpulse = kind === 'main'
      ? letterRotateStart + spinDirection * this.random(22, 42)
      : letterRotateStart + spinDirection * this.random(38, 82);

    const letterRotateMid = kind === 'main'
      ? letterRotateImpulse + spinDirection * this.random(4, 10)
      : letterRotateImpulse + spinDirection * this.random(6, 18);

    const letterRotateEnd = letterRotateMid;

    const duration = kind === 'main'
      ? this.random(1650, 2350)
      : this.random(1350, 2050);

    const opacity = kind === 'main'
      ? this.randomFloat(0.4, 0.84)
      : this.randomFloat(0.36, 0.76);

    const opacityMid = Number((opacity * 0.76).toFixed(2));
    const gradient = this.getRandomGradient();

    element.className = kind === 'main'
      ? 'd-trail__item d-trail__item--main'
      : 'd-trail__item d-trail__item--satellite';

    element.style.left = `${x}px`;
    element.style.top = `${y}px`;

    element.style.setProperty('--size', `${size}px`);

    element.style.setProperty('--tx', `${tx}px`);
    element.style.setProperty('--ty', `${ty}px`);

    element.style.setProperty('--letter-rotate-start', `${letterRotateStart}deg`);
    element.style.setProperty('--letter-rotate-impulse', `${letterRotateImpulse}deg`);
    element.style.setProperty('--letter-rotate-end', `${letterRotateEnd}deg`);

    element.style.setProperty('--start-scale', kind === 'main' ? '0.36' : '0.28');
    element.style.setProperty('--end-scale', kind === 'main' ? '0.82' : '0.76');

    element.style.setProperty('--duration', `${duration}ms`);
    element.style.setProperty('--opacity', `${opacity}`);
    element.style.setProperty('--opacity-mid', `${opacityMid}`);
    element.style.setProperty('--gradient', gradient);

    this.activateParticle(element, duration);
  }

  private getParticleElement(): HTMLElement {
    let element = this.pooledElements.pop();

    if (!element) {
      element = document.createElement('span');
      element.className = 'd-trail__item';
    }

    if (this.activeElements.length >= this.maxParticles) {
      const oldestElement = this.activeElements.shift();

      if (oldestElement) {
        this.recycleParticle(oldestElement);
      }
    }

    this.activeElements.push(element);

    if (!element.isConnected) {
      this.trailRoot.nativeElement.appendChild(element);
    }

    return element;
  }

  private activateParticle(element: HTMLElement, duration: number): void {
    const existingTimeoutId = this.timeoutIds.get(element);

    if (existingTimeoutId) {
      window.clearTimeout(existingTimeoutId);
      this.timeoutIds.delete(element);
    }

    element.classList.remove('d-trail__item--active');

    void element.offsetWidth;

    element.classList.add('d-trail__item--active');

    const timeoutId = window.setTimeout(() => {
      this.recycleParticle(element);
    }, duration + 80);

    this.timeoutIds.set(element, timeoutId);
  }

  private recycleParticle(element: HTMLElement): void {
    const timeoutId = this.timeoutIds.get(element);

    if (timeoutId) {
      window.clearTimeout(timeoutId);
      this.timeoutIds.delete(element);
    }

    element.classList.remove('d-trail__item--active');
    element.remove();

    this.activeElements = this.activeElements.filter(item => item !== element);

    if (this.pooledElements.length < this.maxParticles) {
      this.pooledElements.push(element);
    }
  }

  private getRandomGradient(): string {
    return this.gradients[
      Math.floor(Math.random() * this.gradients.length)
      ];
  }

  private random(min: number, max: number): number {
    return Math.round(min + Math.random() * (max - min));
  }

  private randomFloat(min: number, max: number): number {
    return Number((min + Math.random() * (max - min)).toFixed(2));
  }
}
